import React from 'react';

import { ProgressRow } from './components';

interface ActiveUploadsProps {
    /**
     * A JSX element to render above the list of <ProgressRow> components.
     * @defaultValue null
     */
    header?: JSX.Element | null;
    /**
     * Overall height for the list of rows. If there are more rows than fit in this height, the
     * list will scroll vertically.
     */
    height: number;
    /**
     * A JSX element to render when there are no items in `inProgressTasks`.
     * @defaultValue null
     */
    emptyState?: JSX.Element | null;
    /**
     * List of active {@link UploadFileTask} objects, or any task that extends an UploadFileTask.
     */
    inProgressTasks: UploadFileTask[];
    /**
     * Sizing details for the underlying {@link ProgressRow} components
     */
    rowSize?: {
        /**
         * Row height
         * @defaultValue 50
         */
        height?: number;
        /**
         * Top / bottom margin to space out the rows
         * @defaultValue 2
         */
        marginY?: number;
        /**
         * Row width
         * @defaultValue 800
         */
        width?: number;
    };
}

/**
 * Shows a scrolling list of details for active ${@link UploadFileTask} tasks. Each task is
 * presented as a row with its own GradientProgressBar and a status message that is synced to the
 * task.output.statusMessage.
 * @param props - {@link ActiveUploadProps}
 * @returns JSX
 */
export const ActiveUploads = ({
    header = null,
    height,
    emptyState = null,
    inProgressTasks,
    rowSize,
}: ActiveUploadsProps): JSX.Element => (
    <div
        className="mx-10 flex flex-col items-center overflow-y-auto overflow-x-hidden respond-to-mouse"
        style={{ height }}
    >
        {header}
        {inProgressTasks.map(
            (task: UploadFileTask): JSX.Element => (
                <ProgressRow
                    key={task.taskId}
                    task={task}
                    height={rowSize?.height ?? 50}
                    marginY={rowSize?.marginY ?? 2}
                    width={rowSize?.width ?? 800}
                />
            ),
        )}
        {inProgressTasks.length === 0 && <>{emptyState}</>}
    </div>
);
